import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DollarSign, MessageSquare, ThumbsDown } from 'lucide-react';
import { MAX_PRICE, MIN_PRICE, isPriceOutOfBounds } from '~/utils/moneyBounds';
import { Button } from '~/components/ui/buttons/Button';
import { Input } from '~/components/ui/inputs/Input';
import { TextArea } from '~/components/ui/inputs/TextArea';
import type { Currency, Money, UnitCode } from '~/graphql/graphql';
import { formatMoney, priceUnitLabel } from '~/utils';

export interface PriceNegotiationTallyRecipeLine {
  lengthLabel: string;
  bundleCount: number;
  piecesPerBundle: number;
}

export interface PriceNegotiationCommonData {
  id: string;
  productName: string;
  productLength: string;
  currentSellerPrice: Money | null;
  targetPrice: Money | null;
  unit: UnitCode;
  offerCurrency?: Currency;
  buyerNote: string | null;
  buyerFullName: string;
  buyerOrganization: string;
  sellerOrganization: string;
}

export interface TallyPriceNegotiationData extends PriceNegotiationCommonData {
  isTally: true;
  tallyRecipe: PriceNegotiationTallyRecipeLine[];
}

export interface LengthPriceNegotiationData extends PriceNegotiationCommonData {
  isTally: false;
}

export type PriceNegotiationData =
  TallyPriceNegotiationData | LengthPriceNegotiationData;

export interface PriceNegotiationResponseValues {
  offeredPrice: number;
  sellerNote: string | null;
}

interface PriceNegotiationResponseFormProps {
  data: PriceNegotiationData;
  submitting: boolean;
  loading?: boolean;
  onSubmit: (values: PriceNegotiationResponseValues) => void;
  onDecline: (reason: string | null) => void;
  onCancel?: () => void;
}

export function PriceNegotiationResponseForm({
  data,
  submitting,
  loading = false,
  onSubmit,
  onDecline,
  onCancel,
}: Readonly<PriceNegotiationResponseFormProps>) {
  const { t } = useTranslation('respond-to-price-negotiation');
  const unit = data.unit;

  const [offeredPrice, setOfferedPrice] = useState(
    data.targetPrice ? String(data.targetPrice.amount) : ''
  );
  const [sellerNote, setSellerNotes] = useState('');

  const parsedPrice = parseFloat(offeredPrice) || 0;

  const handleSubmit = () => {
    if (submitting || loading || isPriceOutOfBounds(parsedPrice)) return;
    onSubmit({
      offeredPrice: parsedPrice,
      sellerNote: sellerNote || null,
    });
  };

  return (
    <div className="max-w-2xl mx-auto p-8 bg-neut-light">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-neut-prim">
          {t('respond_to_price_negotiation.form.title')}
        </h1>
        <p className="text-sm text-neut-sec mt-1">
          {t('respond_to_price_negotiation.form.subtitle', {
            buyerFullName: data.buyerFullName,
            buyerOrganization: data.buyerOrganization,
          })}
        </p>
      </div>

      <div className="bg-warning-50 border border-warning-200 rounded-lg p-5 mb-6">
        <h2 className="text-sm font-semibold text-warning-800 uppercase tracking-wide mb-4">
          {t('respond_to_price_negotiation.form.buyer_request.heading')}
        </h2>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-xs text-neut-sec mb-1">
              {t('respond_to_price_negotiation.form.buyer_request.product')}
            </p>
            <p className="text-sm font-medium text-neut-prim">
              {data.productName}
            </p>
            <p className="text-xs text-neut-sec">{data.productLength}</p>
          </div>
          <div>
            <p className="text-xs text-neut-sec mb-1">
              {t(
                'respond_to_price_negotiation.form.buyer_request.listed_price'
              )}
            </p>
            <p className="text-sm font-medium text-neut-prim">
              {data.currentSellerPrice
                ? `${formatMoney(data.currentSellerPrice)}/${priceUnitLabel(unit)}`
                : '-'}
            </p>
          </div>
          <div>
            <p className="text-xs text-neut-sec mb-1">
              {t(
                'respond_to_price_negotiation.form.buyer_request.target_price'
              )}
            </p>
            <p className="text-sm font-medium text-error-500">
              {data.targetPrice
                ? `${formatMoney(data.targetPrice)}/${priceUnitLabel(unit)}`
                : '-'}
            </p>
          </div>
        </div>

        {data.isTally && data.tallyRecipe.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-semibold text-neut-sec mb-2">
              {t(
                'respond_to_price_negotiation.form.buyer_request.tally_recipe_heading'
              )}
            </p>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-neut-sec">
                  <th className="text-left font-normal pb-1">
                    {t(
                      'respond_to_price_negotiation.form.buyer_request.tally_recipe_length'
                    )}
                  </th>
                  <th className="text-right font-normal pb-1">
                    {t(
                      'respond_to_price_negotiation.form.buyer_request.tally_recipe_bundles'
                    )}
                  </th>
                  <th className="text-right font-normal pb-1">
                    {t(
                      'respond_to_price_negotiation.form.buyer_request.tally_recipe_pieces_per_bundle'
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.tallyRecipe.map((line) => (
                  <tr key={line.lengthLabel} className="text-neut-prim">
                    <td className="py-0.5">{line.lengthLabel}</td>
                    <td className="py-0.5 text-right">{line.bundleCount}</td>
                    <td className="py-0.5 text-right">
                      {line.piecesPerBundle}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data.buyerNote && (
          <div className="mt-3 bg-neut-bg-alt/60 border border-warning-100 rounded p-3">
            <p className="text-xs font-semibold text-neut-sec mb-1">
              {t('respond_to_price_negotiation.form.buyer_request.buyer_notes')}
            </p>
            <p className="text-sm text-neut-prim italic">
              &ldquo;{data.buyerNote}&rdquo;
            </p>
          </div>
        )}
      </div>

      <div className="bg-neut-bg-alt border border-neut-border rounded-lg p-5 mb-6 shadow-sm">
        <h2 className="text-sm font-semibold text-neut-prim uppercase tracking-wide mb-4">
          {t('respond_to_price_negotiation.form.your_response.heading')}
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <Input
            id="offeredPrice"
            name="offeredPrice"
            type="number"
            step="0.01"
            min={MIN_PRICE}
            max={MAX_PRICE}
            value={offeredPrice}
            onChange={(_, value) => setOfferedPrice(value)}
            disabled={submitting}
            label={
              <span className="flex items-center gap-1">
                <DollarSign size={12} />
                {t(
                  'respond_to_price_negotiation.form.your_response.offered_price',
                  { unit }
                )}
                {data.offerCurrency && <span>{data.offerCurrency}</span>}
              </span>
            }
          />
        </div>

        <TextArea
          id="sellerNote"
          name="sellerNote"
          rows={3}
          value={sellerNote}
          onChange={(_, value) => setSellerNotes(value)}
          placeholder={t(
            'respond_to_price_negotiation.form.your_response.notes_placeholder'
          )}
          disabled={submitting}
          label={
            <span className="flex items-center gap-1">
              <MessageSquare size={12} />
              {t('respond_to_price_negotiation.form.your_response.notes')}
            </span>
          }
        />
      </div>

      <div className="flex items-center justify-between">
        <Button
          onClick={() => onDecline(sellerNote || null)}
          disabled={submitting}
          variant="destructive-outline"
          width="auto"
        >
          <ThumbsDown size={16} />
          {t('respond_to_price_negotiation.form.actions.decline')}
        </Button>

        <div className="flex items-center gap-3">
          {onCancel && (
            <Button
              onClick={onCancel}
              disabled={submitting}
              variant="secondary"
              width="auto"
            >
              {t('respond_to_price_negotiation.form.actions.cancel')}
            </Button>
          )}
          <Button
            onClick={handleSubmit}
            disabled={
              submitting ||
              loading ||
              parsedPrice <= 0 ||
              isPriceOutOfBounds(parsedPrice)
            }
            loading={submitting || loading}
            width="auto"
          >
            {submitting
              ? t('respond_to_price_negotiation.form.actions.sending')
              : t('respond_to_price_negotiation.form.actions.send')}
          </Button>
        </div>
      </div>
    </div>
  );
}
